import { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import html2canvas from "html2canvas";
import { isEmpty } from "lodash";
import MDButton from "components/MDButton";
import { GetSlideByIdCall } from "../../../redux/api/title.api";
import { useSelector } from "react-redux";
import {
  tileUrlPatternFromSlideFile,
  fetchSlideTileManifest,
  verifySlideTileSource,
  slideTileLayerOptions,
  tileUrlWithExtension,
  slideImageLatLngBounds,
  uploadPhotoLeafletView,
  isUploadPhotoSlide,
  slideFileFromSlide,
  assessUploadPhotoPyramid,
  resolveSlideMasterPreviewUrl,
} from "../showTile/tileUrl";

/**
 * Modal: pan/zoom the slide (same Leaflet pipeline as ShowTile /showTitle)
 * and capture the framed region as a card thumbnail.
 */
export default function SlideThumbnailPickerModal({
  open,
  onClose,
  slide,
  onCapture,
  saving = false,
  privateView = false,
}) {
  const lang = useSelector((state) => state.general?.lang) || "en";
  const mapHostRef = useRef(null);
  const mapRef = useRef(null);
  const masterOverlayRef = useRef(null);
  const mapViewRef = useRef({
    center: [-10.505, -0.09],
    zoom: 2,
  });
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [capturing, setCapturing] = useState(false);
  const [geoJson, setGeoJson] = useState(null);
  const [tileURL, setTileURL] = useState("");

  // Same data load as ShowTile.fetchSlide
  useEffect(() => {
    if (!open) {
      setGeoJson(null);
      setTileURL("");
      setReady(false);
      setError("");
      return undefined;
    }
    let cancelled = false;
    const slideId = slide?.id;
    if (!slideId) {
      setError("No slide selected.");
      return undefined;
    }

    (async () => {
      setReady(false);
      setError("");
      try {
        const res = await GetSlideByIdCall({
          id: slideId,
          lang,
          includePrivatePins: privateView,
          ts: Date.now(),
        });
        if (cancelled) return;
        if (!res || res.status === 400 || isEmpty(res)) {
          // Fall back to the card's slide object if the API call fails.
          const fallbackFile = slideFileFromSlide(slide);
          const fallbackUrl = tileUrlPatternFromSlideFile(fallbackFile);
          if (!fallbackUrl) {
            setError("Could not load this slide.");
            return;
          }
          setGeoJson(slide);
          setTileURL(fallbackUrl);
          return;
        }
        setGeoJson(res);
        setTileURL(tileUrlPatternFromSlideFile(res?.Files?.[0]));
      } catch (err) {
        if (!cancelled) setError(err?.message || "Could not load this slide.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, slide, lang, privateView]);

  // Same Leaflet init path as ShowTile's map useEffect
  useEffect(() => {
    if (!open || !geoJson || !tileURL) return undefined;
    let cancelled = false;
    let map = null;

    const timer = setTimeout(async () => {
      if (cancelled || !mapHostRef.current) return;

      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      if (mapHostRef.current._leaflet_id) {
        mapHostRef.current._leaflet_id = null;
      }

      try {
        const slideMaxZoom =
          typeof geoJson?.max_zoom === "number" && geoJson.max_zoom >= 0
            ? geoJson.max_zoom
            : undefined;
        const uploadPhoto = isUploadPhotoSlide(geoJson);
        const rawManifest = await fetchSlideTileManifest(tileURL);
        if (cancelled) return;
        const { manifest, tileExt: detectedExt } = await verifySlideTileSource(
          tileURL,
          rawManifest
        );
        if (cancelled) return;

        let effectiveMaxZoom = slideMaxZoom;
        let pyramidBuilding = false;
        if (uploadPhoto) {
          const assessment = await assessUploadPhotoPyramid(tileURL, slideMaxZoom);
          if (cancelled) return;
          if (assessment.maxNativeZoom != null) {
            effectiveMaxZoom = assessment.maxNativeZoom;
          }
          pyramidBuilding = Boolean(assessment.building);
        }

        const pyramid = slideTileLayerOptions(manifest, effectiveMaxZoom);
        if (!pyramid) {
          setError("Could not read slide zoom levels.");
          return;
        }
        const layerUrl = tileUrlWithExtension(tileURL, pyramid?.tileExt || detectedExt);
        const worldBounds = [
          [-90, -180],
          [90, 180],
        ];
        const zoomLimits = {
          ...(pyramid?.minZoom != null ? { minZoom: pyramid.minZoom } : {}),
          ...(pyramid?.maxNativeZoom != null ? { maxNativeZoom: pyramid.maxNativeZoom } : {}),
          ...(pyramid?.maxZoom != null ? { maxZoom: pyramid.maxZoom } : {}),
          ...(pyramid?.tileSize ? { tileSize: pyramid.tileSize } : {}),
        };

        map = L.map(mapHostRef.current, {
          center: mapViewRef.current.center,
          zoom: mapViewRef.current.zoom,
          maxBounds: worldBounds,
          maxBoundsViscosity: 1,
          ...(pyramid?.minZoom != null ? { minZoom: pyramid.minZoom } : {}),
          ...(pyramid?.maxZoom != null ? { maxZoom: pyramid.maxZoom } : {}),
          attributionControl: false,
        });
        if (cancelled) {
          map.remove();
          return;
        }
        mapRef.current = map;

        const nativeZoom = pyramid?.maxNativeZoom ?? slideMaxZoom;
        const imageBounds = slideImageLatLngBounds(map, manifest);
        const tileBounds = imageBounds || worldBounds;
        if (imageBounds) {
          map.setMaxBounds(imageBounds.pad(0.05));
          map.fitBounds(imageBounds, { animate: false, padding: [24, 24] });
          const center = map.getCenter();
          mapViewRef.current = {
            center: [center.lat, center.lng],
            zoom: map.getZoom(),
          };
        } else if (uploadPhoto && nativeZoom != null) {
          const z = Math.min(2, Math.max(0, nativeZoom));
          const approx = L.latLngBounds(
            map.unproject([0, 256 * 8], z),
            map.unproject([256 * 32, 0], z)
          );
          if (approx.isValid()) {
            map.setMaxBounds(approx.pad(0.15));
            map.fitBounds(approx, { animate: false, padding: [24, 24] });
            const center = map.getCenter();
            mapViewRef.current = {
              center: [center.lat, center.lng],
              zoom: map.getZoom(),
            };
          } else {
            const photoView = uploadPhotoLeafletView(map, nativeZoom);
            map.setView(photoView.center, photoView.zoom);
            mapViewRef.current = { center: photoView.center, zoom: photoView.zoom };
          }
        }

        map.on("moveend zoomend", () => {
          const center = map.getCenter();
          mapViewRef.current = {
            center: [center.lat, center.lng],
            zoom: map.getZoom(),
          };
        });

        const mainTileLayer = L.tileLayer(layerUrl, {
          attribution: '&copy; <a href="https://microlab.ink">Microlab</a> contributors',
          noWrap: true,
          tms: false,
          bounds: tileBounds,
          crossOrigin: true,
          ...zoomLimits,
        }).addTo(map);
        mainTileLayer.on("tileerror", (event) => {
          console.warn("Thumb picker tile failed:", event?.tile?.src || event?.coords);
        });
        mainTileLayer.on("load", () => {
          setTimeout(() => map.invalidateSize(true), 0);
          setTimeout(() => map.invalidateSize(true), 300);
        });

        if (masterOverlayRef.current) {
          try {
            map.removeLayer(masterOverlayRef.current);
          } catch (_) {
            /* ignore */
          }
          masterOverlayRef.current = null;
        }
        if (uploadPhoto && pyramidBuilding) {
          const masterUrl = await resolveSlideMasterPreviewUrl(slideFileFromSlide(geoJson));
          if (!cancelled && masterUrl) {
            let boundsForOverlay = imageBounds;
            if (!boundsForOverlay || !boundsForOverlay.isValid?.()) {
              const z = Math.min(2, Math.max(0, nativeZoom ?? 1));
              boundsForOverlay = L.latLngBounds(
                map.unproject([0, 256 * 8], z),
                map.unproject([256 * 32, 0], z)
              );
            }
            if (boundsForOverlay?.isValid?.()) {
              const overlay = L.imageOverlay(masterUrl, boundsForOverlay, {
                opacity: 1,
                interactive: false,
                zIndex: 200,
              }).addTo(map);
              masterOverlayRef.current = overlay;
            }
          }
        }

        setTimeout(() => map.invalidateSize(true), 0);
        setTimeout(() => map.invalidateSize(true), 500);
        if (!cancelled) setReady(true);
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to open slide viewer.");
      }
    }, 1000); // same delayed init as ShowTile

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (masterOverlayRef.current) {
        try {
          masterOverlayRef.current.remove();
        } catch (_) {
          /* ignore */
        }
        masterOverlayRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      map = null;
    };
  }, [open, geoJson, tileURL]);

  const handleUseView = async () => {
    if (!mapHostRef.current || !onCapture) return;
    setCapturing(true);
    setError("");
    try {
      const host = mapHostRef.current;
      const crop = host.querySelector("[data-thumb-crop]");
      const hostRect = host.getBoundingClientRect();
      const cropRect = crop?.getBoundingClientRect();
      const x = cropRect ? Math.max(0, cropRect.left - hostRect.left) : hostRect.width * 0.15;
      const y = cropRect ? Math.max(0, cropRect.top - hostRect.top) : hostRect.height * 0.12;
      const width = cropRect ? cropRect.width : hostRect.width * 0.7;
      const height = cropRect ? cropRect.height : hostRect.height * 0.7;

      const canvas = await html2canvas(host, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#0f172a",
        x,
        y,
        width,
        height,
        scale: 2,
        logging: false,
        ignoreElements: (el) =>
          el?.dataset?.thumbUi === "1" || el?.classList?.contains("leaflet-control-container"),
      });

      const blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92)
      );
      if (!blob) throw new Error("Could not capture this view.");
      const file = new File([blob], `slide-thumb-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      await onCapture(file);
    } catch (err) {
      setError(err?.message || "Capture failed. Try zooming in a little and retry.");
    } finally {
      setCapturing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving || capturing ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "#0B1220",
          color: "#E2E8F0",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
          py: 1.5,
          borderBottom: "1px solid rgba(148,163,184,0.18)",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#F8FAFC", m: 0 }}>
            Pick thumbnail from slide
          </Typography>
          <Typography variant="caption" sx={{ color: "#94A3B8" }}>
            Same viewer as the slide page — zoom and move to frame the card thumbnail.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={saving || capturing}
          sx={{ color: "#94A3B8" }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: "relative", bgcolor: "#020617" }}>
        <Box
          ref={mapHostRef}
          id="thumb-picker-map"
          sx={{
            height: { xs: 360, sm: 480 },
            width: "100%",
            position: "relative",
            "& .leaflet-container": {
              height: "100%",
              width: "100%",
              background: "#fff",
            },
          }}
        >
          <Box
            data-thumb-crop
            data-thumb-ui="1"
            sx={{
              pointerEvents: "none",
              position: "absolute",
              left: "50%",
              top: "50%",
              width: "min(72%, 340px)",
              aspectRatio: "4 / 3",
              transform: "translate(-50%, -50%)",
              border: "2px solid rgba(37,99,235,0.95)",
              borderRadius: 2,
              boxShadow: "0 0 0 9999px rgba(15,23,42,0.35)",
              zIndex: 500,
            }}
          />
          {!ready && !error ? (
            <Box
              data-thumb-ui="1"
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 600,
                bgcolor: "rgba(248,250,252,0.72)",
              }}
            >
              <CircularProgress size={36} sx={{ color: "#2563EB" }} />
            </Box>
          ) : null}
        </Box>
        {error ? (
          <Typography sx={{ px: 2, py: 1.5, color: "#FCA5A5", fontSize: 13 }}>{error}</Typography>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 1.5,
          borderTop: "1px solid rgba(148,163,184,0.18)",
          gap: 1,
        }}
      >
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={saving || capturing}
        >
          Cancel
        </MDButton>
        <MDButton
          variant="contained"
          color="primary"
          onClick={handleUseView}
          disabled={!ready || saving || capturing}
        >
          {capturing || saving ? "Saving…" : "Use this view"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
